import React, { useRef, useState } from "react";

// Claude Design Create Event Screen.dc.html:309-310, 395-396 and
// :2557-2563, 2577-2583, 2610-2620: every reorderable wizard list (partners,
// speakers, agenda, tickets, sponsor levels, add-ons; handles also at :697,
// :758, :890, :1566) moves rows with a "⋮⋮" handle in a 24px first column.
// Only the handle starts a drag; the row under the pointer shows #FFF8E7 with
// a #E1A731 top border.

export const EVENT_REORDER_HANDLE_COLUMN = "24px";

export const reorderEventRows = <Row,>(
  rows: readonly Row[],
  fromIndex: number,
  toIndex: number
): Row[] => {
  const next = [...rows];
  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= rows.length) return next;
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export type EventRowReorderProps = {
  handleProps: {
    draggable: true;
    onDragStart: (event: React.DragEvent) => void;
    onDragEnd: () => void;
  };
  rowProps: {
    onDragOver: (event: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (event: React.DragEvent) => void;
    style: React.CSSProperties;
  };
};

export const useEventRowReorder = <Row,>(
  rows: readonly Row[],
  onReorder: (next: Row[]) => void
): ((index: number) => EventRowReorderProps) => {
  // The dragged row is kept in a ref as well: React state updates one render
  // later, so a quick drag could reach the drop row before the state was set
  // and the drop was ignored (live retest, Oct 5 2026).
  const dragIndexRef = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const reset = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (index) => {
    const isDropTarget = dragOverIndex === index && dragIndex !== index;
    return {
      handleProps: {
        draggable: true,
        onDragStart: (event) => {
          try {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", String(index));
          } catch {
            // Some test environments have no dataTransfer.
          }
          dragIndexRef.current = index;
          setDragIndex(index);
        },
        onDragEnd: reset,
      },
      rowProps: {
        onDragOver: (event) => {
          if (dragIndexRef.current === null) return;
          event.preventDefault();
          if (dragOverIndex !== index) setDragOverIndex(index);
        },
        onDragLeave: () => {
          if (dragOverIndex === index) setDragOverIndex(null);
        },
        onDrop: (event) => {
          event.preventDefault();
          const fromIndex = dragIndexRef.current;
          if (fromIndex !== null && fromIndex !== index) {
            onReorder(reorderEventRows(rows, fromIndex, index));
          }
          reset();
        },
        style: {
          background: isDropTarget ? "#FFF8E7" : "#fff",
          borderTopColor: isDropTarget ? "#E1A731" : "#ECECEC",
        },
      },
    };
  };
};

// MUI table rows draw no top border of their own, so the drop line needs one.
export const eventReorderTableRowProps = (
  rowProps: EventRowReorderProps["rowProps"]
): EventRowReorderProps["rowProps"] => ({
  ...rowProps,
  style: { ...rowProps.style, borderTopStyle: "solid", borderTopWidth: 1 },
});

export const EventReorderHandle = (
  props: EventRowReorderProps["handleProps"]
): React.ReactElement => (
  <span
    {...props}
    title="Drag to reorder"
    className="cursor-grab select-none text-[14px] tracking-[2px] text-[#9E9E9E]"
  >
    ⋮⋮
  </span>
);
